import { Button, DatePicker, Form, FormInstance, Input, InputNumber, Select, Space } from 'antd'
import dayjs from 'dayjs'
import CompanySelect from './CompanySelect'
import UserSelect from './UserSelect'
import VNAddressSelect from './VNAddressSelect'
import { IOutlet, OUTLET_SEGMENT_OPTIONS, OUTLET_STATUS_OPTIONS, OUTLET_TYPE_OPTIONS } from '../../models/outlet.modal'

type Props = {
    form: FormInstance
    initial?: Partial<IOutlet>
    onSubmit: (values: any) => void
    submitLabel?: string
    onCancel?: () => void
}

// Shared create/update form for outlet.
// cpCode is required per Plan 3 B4 — prevents pricing cascade errors when adding pricing later.
export default function OutletForm({ form, initial, onSubmit, submitLabel = 'Xác nhận', onCancel }: Props) {
    const initialValues = initial ? {
        ...initial,
        firstSeenDate: initial.firstSeenDate ? dayjs(initial.firstSeenDate) : undefined,
    } : {}

    const handleFinish = (values: any) => {
        const payload = {
            ...values,
            firstSeenDate: values.firstSeenDate ? dayjs(values.firstSeenDate).format('YYYY-MM-DD') : undefined,
            ownerUserId: values.ownerUserId ? Number(values.ownerUserId) : undefined,
            expectedMonthly: values.expectedMonthly != null ? Number(values.expectedMonthly) : undefined,
        }
        onSubmit(payload)
    }

    return (
        <Form
            form={form}
            layout='vertical'
            initialValues={initialValues}
            onFinish={handleFinish}
            autoComplete='off'
        >
            <Form.Item name='name' label='Tên đại lý' rules={[{ required: true, message: 'Vui lòng nhập tên đại lý!' }]}>
                <Input />
            </Form.Item>
            <Form.Item name='code' label='Mã đại lý' tooltip='Mã nội bộ (tùy chọn). Nếu bỏ trống, hệ thống sinh ID tự động.'>
                <Input />
            </Form.Item>
            <Form.Item name='cpCode' label='Công ty (cpCode)' rules={[{ required: true, message: 'Vui lòng chọn công ty!' }]}
                tooltip='Bắt buộc — dùng để cascade pricing / package theo công ty.'>
                <CompanySelect placeholder='Chọn công ty' />
            </Form.Item>
            <Form.Item name='phone' label='Số điện thoại'>
                <Input />
            </Form.Item>
            <Form.Item name='address' label='Địa chỉ'>
                <Input />
            </Form.Item>
            {/* Register 4 fields so antd Form tracks + includes them in onFinish(values).
                Bare setFieldsValue inside VNAddressSelect doesn't register the field. */}
            <Form.Item name='region' hidden><Input /></Form.Item>
            <Form.Item name='province' hidden><Input /></Form.Item>
            <Form.Item name='district' hidden><Input /></Form.Item>
            <Form.Item name='ward' hidden><Input /></Form.Item>
            <Form.Item shouldUpdate style={{ marginBottom: 24 }}>
                {({ getFieldValue, setFieldsValue }) => (
                    <VNAddressSelect
                        region={getFieldValue('region')}
                        province={getFieldValue('province')}
                        district={getFieldValue('district')}
                        ward={getFieldValue('ward')}
                        onChange={(v) => setFieldsValue(v)}
                    />
                )}
            </Form.Item>
            <Space style={{ display: 'flex', width: '100%' }} size='middle'>
                <Form.Item name='outletType' label='Loại đại lý' style={{ flex: 1, minWidth: 200 }}>
                    <Select allowClear options={OUTLET_TYPE_OPTIONS} placeholder='Chọn loại' />
                </Form.Item>
                <Form.Item name='segment' label='Phân khúc' style={{ flex: 1, minWidth: 200 }}>
                    <Select allowClear options={OUTLET_SEGMENT_OPTIONS} placeholder='Chọn phân khúc' />
                </Form.Item>
                <Form.Item name='status' label='Trạng thái' style={{ flex: 1, minWidth: 200 }}>
                    <Select allowClear options={OUTLET_STATUS_OPTIONS} placeholder='Chọn trạng thái' />
                </Form.Item>
            </Space>
            <Form.Item name='ownerUserId' label='Nhân sự phụ trách'>
                <UserSelect placeholder='Chọn nhân sự' />
            </Form.Item>
            <Form.Item name='parentOutletId' label='ID đại lý cha (nếu có)'>
                <Input placeholder='UUID của đại lý cha' />
            </Form.Item>
            <Form.Item name='expectedMonthly' label='Sản lượng SIM/tháng ước tính'>
                <InputNumber style={{ width: '100%' }} min={0} />
            </Form.Item>
            <Form.Item name='competitors' label='Đối thủ đang bán'>
                <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item name='rejectReason' label='Lý do từ chối (nếu có)'>
                <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item name='firstSeenDate' label='Ngày tiếp cận đầu tiên'>
                <DatePicker style={{ width: '100%' }} format='DD/MM/YYYY' />
            </Form.Item>
            <Form.Item name='note' label='Ghi chú'>
                <Input.TextArea rows={3} />
            </Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                {onCancel && <Button onClick={onCancel}>Huỷ</Button>}
                <Button type='primary' htmlType='submit'>{submitLabel}</Button>
            </div>
        </Form>
    )
}
